import { type LoaderFunctionArgs } from "react-router";

import {
  DashboardRunLetterWindowDocument,
  DashboardRunReportReviewDocument,
} from "@app_schema";

import { client } from "@application/config/apollo";

import {
  DEFAULT_LETTER_SORT,
  LETTER_WINDOW_SIZE,
} from "@application/components/dashboard/runs/report_review/constants";

export type DashboardRunReportReviewLoaderData = {
  projectID: string;
  runId: string;
};

// The `:id` parent's project query is ~1.5s on a large case and this route's
// element cannot mount until it settles, so the page's own queries used to
// start only once it had. Warming them here runs all three together.
//
// Not awaited: the loader still returns on the spot with the breadcrumb, and
// the page reads these from the cache when it mounts. A rejection is dropped
// because the page re-issues both queries itself and surfaces the error.
//
// The variables must equal the page's first render or Apollo misses the cache
// and each query runs twice. First render carries no search, no filters and no
// letter scope (all plain useState defaults, nothing from the URL).
const warmPageQueries = (id: string) => {
  const unfiltered = {
    search: undefined,
    providers: undefined,
    docCategories: undefined,
    reviewStatuses: undefined,
    baselineOnly: false,
  };

  void client
    .query({
      query: DashboardRunReportReviewDocument,
      variables: { id, ...unfiltered },
    })
    .catch(() => undefined);

  void client
    .query({
      query: DashboardRunLetterWindowDocument,
      variables: {
        id,
        letterOffset: 0,
        letterLimit: LETTER_WINDOW_SIZE,
        sort: DEFAULT_LETTER_SORT,
        ...unfiltered,
      },
    })
    .catch(() => undefined);

  // Same specifier the route lazy-loads, so this fetches that chunk rather
  // than waiting for the element to be reached.
  void import("@application/components/dashboard/runs/report_review");
};

export const DASHBOARD_RUN_REPORT_REVIEW_LOADER = (
  props: LoaderFunctionArgs,
): DashboardRunReportReviewLoaderData => {
  const { id: projectID, runId } = props.params;
  if (!projectID) throw new Error("undefined projectID");
  if (!runId) throw new Error("undefined runId");

  warmPageQueries(runId);

  return { projectID, runId };
};
